// UserIdLayout.jsx
import React from 'react';
import { useLoaderData, Outlet, data, redirect } from 'react-router';

import { createInitialStoreWithRequest } from '../createInitialStore.js';
import fetchUserDetails from '../action/fetchUser.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import errorRoute from '../resource/errorRoute.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import prepareUserProfileMeta from '../resource/prepareUserProfileMeta.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { generateLangPath } from '../resource/langPath.js';
import useSSRDataMerge, { resetSSRDataMerge } from '../hook/useSSRDataMerge.js';
import UniversalComponent from '../component/UniversalComponent.jsx';
import getUserData from '../selector/getUserData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getUserProfileUrl from '../resource/getUserProfileUrl.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import {
  getUserPostFeedNameWithQuery,
  fetchFeedTotalCount,
} from '../resource/fetchFeedTotalCount.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const shouldRevalidate = ({ currentParams: curr, nextParams: next }) =>
  curr.listType !== next.listType || curr.publishStatus !== next.publishStatus;

export const loader = async ({
  context,
  params,
  request,
  i18nInstance,
  language,
}) => {
  const log = legacySSRLog.extend('UserIdLayout loader()');
  const errorLog = legacySSRError.extend('UserIdLayout loader()');
  log('start', { context, params, request });

  // Reset to re-merge SSR data
  resetSSRDataMerge();

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });
    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    // Get state and feed name to prepare for parallel API calls
    const state = store.getState();
    const feedNameWithQuery = getUserPostFeedNameWithQuery({
      state,
      userId: params.userId,
      listType: params.listType,
    });

    // Fetch user details and message count in parallel
    const [, messageCountResult] = await Promise.allSettled([
      store.dispatch(fetchUserDetails({ id: params.userId, httpProxyHeaders })),
      feedNameWithQuery
        ? fetchFeedTotalCount({ feedNameWithQuery, httpProxyHeaders })
        : Promise.resolve(null),
    ]);

    const finalState = store.getState();

    // Canonical profile URL is /u/:username. Redirect /user/:userId here so
    // Google indexes a single URL per user profile and avoids duplicate-page
    // signals between the two URL shapes.
    const username = getUserData(finalState, params.userId, 'username');
    if (username) {
      const pathSegments = [
        username,
        params.listType,
        params.publishStatus,
      ].filter(Boolean);
      const requestUrl = new URL(request.url);
      // Keep the language prefix (/en, /ja, /cn) so a non-default-language
      // request isn't redirected to the default-language canonical URL.
      const targetPath = generateLangPath(
        `/u/${pathSegments.join('/')}`,
        language
      );
      return redirect(`${targetPath}${requestUrl.search}`, 308);
    }

    const storeData = finalState.toJS();
    log('fetched data', { store: storeData });

    // Extract message count from result
    const messageCount =
      messageCountResult.status === 'fulfilled' ? messageCountResult.value : 0;

    const availableLanguages = getRemoteConfigData(
      finalState,
      SUPPORTED_LANGUAGES
    );

    const canonicalPath = getUserProfileUrl({
      userId: params.userId,
      username,
      subPath: params.listType,
    });

    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
      language,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareUserProfileMeta({
      state: finalState,
      userId: params.userId,
      listType: params.listType,
      hostname: request?.headers?.get('host') || 'swag.live',
      includeJsonLd: true,
      canonical,
      alternates,
      messageCount,
      i18n: i18nInstance,
    });

    log('meta data prepared', { meta });

    const loaderData = {
      data: { store: { users: storeData.users } },
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };

    // User not found: respond with HTTP 404 during SSR so crawlers get the
    // correct status instead of a 200, then render the 404 page.
    const userFetchError = getNetworkingData(
      finalState,
      ['user', params.userId],
      'error'
    );
    const isUserNotFound =
      !!userFetchError && 404 === errorRoute(userFetchError.message).status;

    return isUserNotFound
      ? data({ ...loaderData, isUserNotFound: true }, { status: 404 })
      : loaderData;
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const UserIdLayout = () => {
  const { data, error, isUserNotFound } = useLoaderData();

  useSSRDataMerge({
    componentName: 'UserIdLayout',
    loaderData: data,
    loaderError: error,
  });

  if (isUserNotFound) {
    return (
      <UniversalComponent
        filename="Container_NotFoundPage"
        importer={
          /* #__LOADABLE__ */ () => import('../container/NotFoundPage.js')
        }
      />
    );
  }

  return <Outlet />;
};

export default UserIdLayout;
